/**
 * IR Variant Popup
 * Fetches live product data (prices, variants) from Shopify AJAX API.
 * ATC flow: add to cart -> update cart bubble + open drawer (same as ir-consistency-pays-off).
 */

(function () {
  "use strict";

  // ── Locale prefix helper ──────────────────────────────────────────────────

  function getLocalePrefix() {
    const m = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
    return m ? "/" + m[1] : "";
  }

  // ── Money formatter ───────────────────────────────────────────────────────

  function formatMoney(cents, currencyCode) {
    if (window.Shopify && window.Shopify.formatMoney && window.Shopify.money_format) {
      const fmt = window.Shopify.formatMoney(cents, window.Shopify.money_format);
      if (fmt) return fmt;
    }
    const currency = currencyCode
      || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
      || "IDR";
    try {
      return new Intl.NumberFormat("id-ID", {
        style: "currency", currency,
        minimumFractionDigits: 0, maximumFractionDigits: 0,
      }).format(cents / 100);
    } catch (_) {
      return currency + " " + (cents / 100).toLocaleString();
    }
  }

  function calcDiscountPct(price, comparePrice) {
    if (!comparePrice || comparePrice <= price) return 0;
    return Math.round(((comparePrice - price) / comparePrice) * 100);
  }

  // ── Clean handle ──────────────────────────────────────────────────────────

  function cleanHandle(raw) {
    if (!raw) return "";
    try {
      const url = new URL(raw);
      const parts = url.pathname.replace(/\/$/, "").split("/");
      return parts[parts.length - 1].split("?")[0];
    } catch (_) {
      return raw.replace(/\/$/, "").split("/").pop().split("?")[0].split("#")[0];
    }
  }

  // ── Fetch product (locale-aware) ──────────────────────────────────────────

  async function fetchProduct(rawHandle) {
    const handle = cleanHandle(rawHandle);
    const locale = getLocalePrefix();
    const paths = [];
    if (locale) paths.push(locale + "/products/" + handle + ".js");
    paths.push("/products/" + handle + ".js");

    let lastErr = null;
    for (const path of paths) {
      try {
        const res = await fetch(path);
        if (res.ok) return res.json();
        lastErr = new Error("Product not found: " + handle);
      } catch (e) { lastErr = e; }
    }
    throw lastErr || new Error("Product not found: " + handle);
  }

  // ── Add to Cart (locale-aware) ────────────────────────────────────────────

  async function addToCart(variantId, quantity) {
    const locale = getLocalePrefix();
    const paths = [];
    if (locale) paths.push(locale + "/cart/add.js");
    paths.push("/cart/add.js");

    let lastErr = null;
    for (const path of paths) {
      try {
        const res = await fetch(path, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: Number(variantId), quantity: quantity || 1 }),
        });
        if (res.ok) return res.json();
        const body = await res.json().catch(() => ({}));
        lastErr = new Error(body.description || body.message || "Could not add to cart");
      } catch (e) { lastErr = e; }
    }
    throw lastErr || new Error("Could not add to cart");
  }

  // ── Update cart sections + open drawer (same as ir-consistency-pays-off) ──

  async function refreshCartAndOpenDrawer() {
    const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      || (getLocalePrefix() + "/");
    const sep = rootUrl.endsWith("/") ? "" : "/";
    const sectionsUrl = rootUrl + sep + "?sections=cart-drawer,cart-icon-bubble";

    try {
      const res = await fetch(sectionsUrl, { headers: { "Accept": "application/json" } });
      const sections = await res.json();

      if (sections["cart-icon-bubble"]) {
        const doc = new DOMParser().parseFromString(sections["cart-icon-bubble"], "text/html");
        const newBubble = doc.querySelector("#cart-icon-bubble");
        const curBubble = document.querySelector("#cart-icon-bubble");
        if (newBubble && curBubble) curBubble.replaceWith(newBubble);
      }

      if (sections["cart-drawer"]) {
        const doc = new DOMParser().parseFromString(sections["cart-drawer"], "text/html");
        const newDrawer = doc.querySelector("cart-drawer");
        const curDrawer = document.querySelector("cart-drawer");
        if (newDrawer && curDrawer) curDrawer.replaceWith(newDrawer);
      }
    } catch (_) {}

    // Open the fresh drawer
    const drawer = document.querySelector("cart-drawer");
    if (drawer) {
      if (typeof drawer.open === "function") drawer.open();
      else drawer.classList.add("active");
    }

    document.dispatchEvent(new CustomEvent("cart:updated"));
  }

  // ── Build popup DOM ───────────────────────────────────────────────────────

  function buildPopup() {
    if (document.getElementById("ir-vp-overlay")) return;

    const overlay = document.createElement("div");
    overlay.id = "ir-vp-overlay";
    overlay.className = "ir-vp-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "Select product");
    overlay.innerHTML = `
      <div class="ir-vp-sheet" id="ir-vp-sheet">
        <div id="ir-vp-content">
          <div class="ir-vp-loading">
            <div class="ir-vp-loading-spinner"></div>
            Loading\u2026
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    overlay.addEventListener("click", (e) => { if (e.target === overlay) closePopup(); });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && overlay.classList.contains("is-open")) closePopup();
    });
  }

  function openPopup() {
    const overlay = document.getElementById("ir-vp-overlay");
    if (!overlay) return;
    overlay.classList.add("is-open");
    document.body.style.overflow = "hidden";
  }

  function closePopup() {
    const overlay = document.getElementById("ir-vp-overlay");
    if (!overlay) return;
    overlay.classList.remove("is-open");
    document.body.style.overflow = "";
  }

  // ── Render variants ───────────────────────────────────────────────────────

  function renderContent(product, variantConfig, popupSettings) {
    const content = document.getElementById("ir-vp-content");
    if (!content) return;

    const currency = product.currency
      || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
      || "IDR";

    const variants = product.variants.filter((v) => v.available !== false);

    const getConfig = (v, idx) => (!variantConfig || !variantConfig.length) ? {} : (variantConfig[idx] || {});

    let selectedIdx = 0;
    if (variantConfig) {
      const i = variantConfig.findIndex((c) => c.highlight === true);
      if (i >= 0 && variants[i]) selectedIdx = i;
    }

    const title        = popupSettings.title        || "Select the Quantity";
    const subtitle     = popupSettings.subtitle     || "Upgrade to bigger bundles and save more";
    const proofText    = popupSettings.proof_text   || "";
    const securityText = popupSettings.security_text || "Taxes and shipping calculated at checkout";
    const ctaLabel     = popupSettings.cta_label    || "ADD TO CART";

    content.innerHTML = `
      <div class="ir-vp-header">
        <div>
          <div class="ir-vp-title">${title}</div>
          ${subtitle ? `<div class="ir-vp-subtitle">${subtitle}</div>` : ""}
        </div>
        <button class="ir-vp-close-btn" type="button" aria-label="Close" id="ir-vp-close">
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path d="M18.3 5.7L12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7 4.3 4.3l6.3 6.3 6.3-6.3 1.4 1.4z" fill="currentColor"/>
          </svg>
        </button>
      </div>

      <div class="ir-vp-variants" id="ir-vp-variants">
        ${variants.map((v, idx) => {
          const cfg = getConfig(v, idx);
          const price = v.price;
          const comparePrice = v.compare_at_price;
          const discountPct = calcDiscountPct(price, comparePrice);
          const imgSrc = product.images && product.images[0] ? product.images[0] : null;

          return `
            <div
              class="ir-vp-variant${idx === selectedIdx ? " is-selected" : ""}"
              data-variant-id="${v.id}"
              role="radio"
              aria-checked="${idx === selectedIdx}"
              tabindex="0"
            >
              ${cfg.badge ? `<span class="ir-vp-variant-badge${cfg.highlight ? " ir-vp-badge--highlight" : ""}">${cfg.badge}</span>` : ""}
              <div style="position:relative;flex:0 0 auto;">
                ${imgSrc
                  ? `<img class="ir-vp-variant-img" src="${imgSrc}" alt="${v.title}" loading="lazy">`
                  : `<div class="ir-vp-variant-img-placeholder">
                      <svg viewBox="0 0 24 24" width="28" height="28"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" fill="currentColor"/></svg>
                    </div>`
                }
                ${discountPct > 0 ? `<div class="ir-vp-discount-badge">${discountPct}%<br>Discount</div>` : ""}
              </div>
              <div class="ir-vp-variant-info">
                <div class="ir-vp-variant-name">${v.title}</div>
                ${cfg.extra_label ? `<div class="ir-vp-variant-meta">${cfg.extra_label}</div>` : ""}
              </div>
              <div class="ir-vp-variant-prices">
                <span class="ir-vp-price">${formatMoney(price, currency)}</span>
                ${comparePrice && comparePrice > price
                  ? `<span class="ir-vp-compare-price">${formatMoney(comparePrice, currency)}</span>` : ""}
                ${cfg.per_unit_label && cfg.qty && cfg.qty > 1
                  ? `<span class="ir-vp-per-unit">${cfg.per_unit_label}: ${formatMoney(Math.round(price / cfg.qty), currency)}</span>` : ""}
              </div>
            </div>
          `;
        }).join("")}
      </div>

      ${proofText ? `<div class="ir-vp-proof" id="ir-vp-proof"${selectedIdx === 0 ? ' style="display:none"' : ''}>${proofText}</div>` : ""}
      <button class="ir-vp-cta" type="button" id="ir-vp-atc">${ctaLabel}</button>
      ${securityText ? `<div class="ir-vp-security">${securityText}</div>` : ""}
      <div class="ir-vp-payments">
        <img src="//islandrituals.id/cdn/shop/files/Rectangle_100.png?v=1766946187&width=220" alt="Visa" loading="lazy">
        <img src="//islandrituals.id/cdn/shop/files/Rectangle_101.png?v=1766946187&width=220" alt="Shopee" loading="lazy">
        <img src="//islandrituals.id/cdn/shop/files/Rectangle_102.png?v=1766946187&width=220" alt="QRIS" loading="lazy">
        <img src="//islandrituals.id/cdn/shop/files/Rectangle_103.png?v=1766946187&width=220" alt="DANA" loading="lazy">
        <img src="//islandrituals.id/cdn/shop/files/Rectangle_104.png?v=1766946187&width=220" alt="MasterCard" loading="lazy">
        <img src="//islandrituals.id/cdn/shop/files/Rectangle_105.png?v=1766946187&width=220" alt="Mandiri" loading="lazy">
      </div>
    `;

    let currentVariantId = variants[selectedIdx] ? String(variants[selectedIdx].id) : null;

    const variantEls = content.querySelectorAll(".ir-vp-variant");
    variantEls.forEach((el) => {
      const select = () => {
        variantEls.forEach((v) => { v.classList.remove("is-selected"); v.setAttribute("aria-checked", "false"); });
        el.classList.add("is-selected");
        el.setAttribute("aria-checked", "true");
        currentVariantId = el.dataset.variantId;
        const proofEl = content.querySelector("#ir-vp-proof");
        if (proofEl) proofEl.style.display = Array.from(variantEls).indexOf(el) === 0 ? "none" : "";
      };
      el.addEventListener("click", select);
      el.addEventListener("keydown", (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); select(); } });
    });

    content.querySelector("#ir-vp-close").addEventListener("click", closePopup);

    // ── ATC handler ───────────────────────────────────────────────────────────
    const atcBtn = content.querySelector("#ir-vp-atc");
    atcBtn.addEventListener("click", async () => {
      if (!currentVariantId) return;

      atcBtn.disabled = true;
      atcBtn.classList.add("is-loading");
      atcBtn.innerHTML = `
        <span style="display:inline-block;width:18px;height:18px;border:2.5px solid rgba(255,255,255,0.35);border-top-color:#fff;border-radius:50%;animation:ir_vp_spin 600ms linear infinite;margin-right:8px;vertical-align:middle;"></span>
        Adding\u2026
      `;

      try {
        // 1. Add to cart
        await addToCart(currentVariantId, 1);

        // 2. Close variant popup
        closePopup();

        // 3. Redirect to checkout
        const checkoutUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
          ? window.Shopify.routes.root.replace(/\/$/, "") + "/checkout"
          : getLocalePrefix() + "/checkout";
        window.location.href = checkoutUrl;

      } catch (err) {
        atcBtn.disabled = false;
        atcBtn.classList.remove("is-loading");
        atcBtn.style.background = "#c0392b";
        atcBtn.textContent = err.message || "Error \u2014 try again";
        console.error("[IR Variant Popup] ATC error:", err);
        setTimeout(() => {
          atcBtn.textContent = ctaLabel;
          atcBtn.style.background = "";
        }, 2500);
      }
    });
  }

  // ── Error render ──────────────────────────────────────────────────────────

  function renderError(msg) {
    const content = document.getElementById("ir-vp-content");
    if (!content) return;
    content.innerHTML = `
      <div class="ir-vp-header">
        <div class="ir-vp-title">Oops</div>
        <button class="ir-vp-close-btn" type="button" aria-label="Close" id="ir-vp-close">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path d="M18.3 5.7L12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7 4.3 4.3l6.3 6.3 6.3-6.3 1.4 1.4z" fill="currentColor"/>
          </svg>
        </button>
      </div>
      <div class="ir-vp-error">${msg || "Could not load product. Please try again."}</div>
    `;
    content.querySelector("#ir-vp-close").addEventListener("click", closePopup);
  }

  // ── Trigger handler ───────────────────────────────────────────────────────

  async function handleTrigger(trigger) {
    const handle = trigger.dataset.productHandle;
    if (!handle) { console.warn("[IR Variant Popup] Missing data-product-handle"); return; }
    console.log("[IR Variant Popup] Loading:", cleanHandle(handle));

    let variantConfig = null;
    try {
      const raw = trigger.dataset.variantConfig;
      if (raw) variantConfig = JSON.parse(raw);
    } catch (e) { console.warn("[IR Variant Popup] Bad variant config JSON", e); }

    const popupSettings = {
      title:         trigger.dataset.popupTitle    || "",
      subtitle:      trigger.dataset.popupSubtitle || "",
      proof_text:    trigger.dataset.proofText     || "",
      security_text: trigger.dataset.securityText  || "",
      cta_label:     trigger.dataset.ctaLabel      || "",
    };

    buildPopup();
    openPopup();

    const content = document.getElementById("ir-vp-content");
    if (content) content.innerHTML = `<div class="ir-vp-loading"><div class="ir-vp-loading-spinner"></div>Loading\u2026</div>`;

    try {
      const product = await fetchProduct(handle);
      renderContent(product, variantConfig, popupSettings);
    } catch (err) {
      renderError(err.message);
    }
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  document.addEventListener("DOMContentLoaded", () => {
    document.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-ir-vp-trigger]");
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      handleTrigger(trigger);
    });
  });

  // ── Global entry point for sticky ATC bar ────────────────────────────────
  window.irVpOpen = function(handle, variantConfig, popupSettings) {
    const synth = { dataset: {
      productHandle: handle || "",
      variantConfig: variantConfig ? JSON.stringify(variantConfig) : "",
      popupTitle: (popupSettings && popupSettings.title) || "",
      popupSubtitle: (popupSettings && popupSettings.subtitle) || "",
      proofText: (popupSettings && popupSettings.proof_text) || "",
      securityText: (popupSettings && popupSettings.security_text) || "",
      ctaLabel: (popupSettings && popupSettings.cta_label) || "",
    }};
    handleTrigger(synth);
  };

})();
